import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/SaveAsRounded';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import XtramModal, { XtramButton, XtramField, XtramInput } from '../Widgets/XtramModal/XtramModal';
import ExternalTransactionService from '../../Services/externalTransaction.service';
import NoDocumentsPdf from '../../assests/pdf/NoDocument.pdf';
import styles from './EditExternalTransaction.module.scss';

const EditExternalTransactionDialog = ({
    editBankTransactionDialog,
    handleCloseBankEditTransactionDialog,
    editedTransaction,
    changeNewBankTransaction,
     handleEditBankTransaction,
    handleDelete,
    selectedTBank,
    token,
    viewOnly = false
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const fileInputRef = useRef(null);
    const [documentLoading, setDocumentLoading] = useState(false);
    const [resolvedDocumentUrl, setResolvedDocumentUrl] = useState(null);
    const [showDocument, setShowDocument] = useState(false);

    useEffect(() => {
        if (!editedTransaction.transactionDate) {
            changeNewBankTransaction('transactionDate', dayjs());
        }
    }, [editBankTransactionDialog, editedTransaction]);

    // Always show the document automatically whenever one exists (and in view-only mode). The eye
    // button can still toggle it off afterwards.
    useEffect(() => {
        if (editBankTransactionDialog) {
            setShowDocument(viewOnly || !!editedTransaction?.documentUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editBankTransactionDialog, viewOnly, editedTransaction?.documentUrl]);

    const handleClose = () => {
        handleCloseBankEditTransactionDialog();
    }

    const documentCaseId = selectedTBank?.caseId || editedTransaction?.caseId;

    // Resolve a viewable (http) URL for the inline preview. Presigned URLs are used directly;
    // a stored S3 object key is exchanged for a fresh presigned URL.
    useEffect(() => {
        let active = true;
        const resolve = async () => {
            const url = editedTransaction?.documentUrl;
            if (!url) {
                setResolvedDocumentUrl(null);
                return;
            }
            if (/^https?:\/\//i.test(url)) {
                setResolvedDocumentUrl(url);
                return;
            }
            if (!editedTransaction?.id || !documentCaseId) {
                setResolvedDocumentUrl(null);
                return;
            }
            try {
                const response = await ExternalTransactionService.GetDocumentUrlById(
                    editedTransaction.id,
                    documentCaseId,
                    token
                );
                if (response.ok) {
                    const result = await response.json();
                    if (active) setResolvedDocumentUrl(result.url || null);
                }
            } catch (error) {
                if (active) setResolvedDocumentUrl(null);
            }
        };
        if (editBankTransactionDialog) resolve();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editBankTransactionDialog, editedTransaction?.documentUrl, editedTransaction?.id]);

    const handleViewDocument = async () => {
        if (!editedTransaction?.documentUrl) return;
        // Prefer the already-resolved viewable URL; otherwise fall back to the raw value or resolve it.
        if (resolvedDocumentUrl && /^https?:\/\//i.test(resolvedDocumentUrl)) {
            window.open(resolvedDocumentUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        // If we already hold an http(s) URL (e.g. presigned), open it directly; otherwise resolve a
        // fresh pre-signed URL from the stored object key.
        if (/^https?:\/\//i.test(editedTransaction.documentUrl)) {
            window.open(editedTransaction.documentUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (!editedTransaction?.id || !documentCaseId) return;
        setDocumentLoading(true);
        try {
            const response = await ExternalTransactionService.GetDocumentUrlById(
                editedTransaction.id,
                documentCaseId,
                token
            );
            if (response.ok) {
                const result = await response.json();
                if (result.url) {
                    window.open(result.url, '_blank', 'noopener,noreferrer');
                }
            }
        } catch (error) {
            console.error('Error opening document:', error);
        } finally {
            setDocumentLoading(false);
        }
    };

    const handleUploadDocumentClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDocumentSelected = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ''; // allow re-selecting the same file
        if (!file) return;
        if (!editedTransaction?.id || !documentCaseId) {
            toast.error(t('FinsiteFetchFailed'));
            return;
        }
        setDocumentLoading(true);
        try {
            const response = await ExternalTransactionService.UploadDocument(
                editedTransaction.id,
                documentCaseId,
                file,
                token
            );
            if (response.ok) {
                const result = await response.json();
                changeNewBankTransaction('documentUrl', result.presignedUrl);
                toast.success(t('documentUploadedSuccess'));
            } else {
                const errorText = await response.text();
                toast.error(`${t('ErrorUploadingFiles')}: ${errorText}`);
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            toast.error(t('ErrorUploadingFiles'));
        } finally {
            setDocumentLoading(false);
        }
    };

    const handleDeleteDocument = async () => {
        if (!editedTransaction?.id || !documentCaseId) return;
        setDocumentLoading(true);
        try {
            const response = await ExternalTransactionService.DeleteDocumentUrl(
                editedTransaction.id,
                documentCaseId,
                token
            );
            if (response.ok) {
                changeNewBankTransaction('documentUrl', null);
                toast.success(t('DocumentUrlDeletedSuccessfully'));
            } else {
                const errorText = await response.text();
                toast.error(`${t('ErrorDeletingDocumentUrl')}: ${errorText}`);
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error(t('ErrorDeletingDocumentUrl'));
        } finally {
            setDocumentLoading(false);
        }
    };

    const isFormValid = !!(
        editedTransaction?.description &&
        editedTransaction.reference1 &&
        editedTransaction.transactionDate &&
        editedTransaction.amount
    );

    const hasDocument = !!editedTransaction?.documentUrl || viewOnly;
    const showDocumentPane = hasDocument && showDocument;
    const iframeSrc = resolvedDocumentUrl
        ? `${resolvedDocumentUrl}#page=1&view=FitH`
        : NoDocumentsPdf;

    const amountLabel = selectedTBank?.foreignCurrencyCode != null ? t('CurrencyAmount') : t('Amount');

    return (
        <XtramModal
            open={editBankTransactionDialog}
            onClose={handleClose}
            title={viewOnly ? t('ViewBankCommand') : t('EditBankCommand')}
            direction={direction}
            wide={!showDocumentPane}
            expanded={showDocumentPane}
            footer={viewOnly ? null : (
                <>
                    <XtramButton variant="danger" onClick={handleDelete}>
                        <DeleteIcon fontSize="small" />
                        {t('Delete')}
                    </XtramButton>
                    <XtramButton variant="secondary" onClick={handleClose}>{t('Cancel')}</XtramButton>
                    <XtramButton onClick={handleEditBankTransaction} disabled={!isFormValid}>
                        <SaveIcon fontSize="small" />
                        {t('Save')}
                    </XtramButton>
                </>
            )}
        >
            <div className={`${styles.layout} ${showDocumentPane ? styles.layoutWithPreview : ''}`}>
                <div className={styles.form}>
                    <XtramField label={t('Description')} required>
                        <XtramInput
                            direction={direction}
                            disabled={viewOnly}
                            placeholder={t('Description')}
                            value={editedTransaction?.description || ''}
                            onChange={(e) => changeNewBankTransaction('description', e.target.value)}
                        />
                    </XtramField>
                    <XtramField label={t('Reference')} required>
                        <XtramInput
                            direction={direction}
                            disabled={viewOnly}
                            placeholder={t('Reference')}
                            value={editedTransaction?.reference1 || editedTransaction?.reference || ''}
                            onChange={(e) => {
                                const next = e.target.value;
                                changeNewBankTransaction('reference1', next);
                                changeNewBankTransaction('reference', next);
                            }}
                        />
                    </XtramField>
                    <XtramField label={t('TransactionDate')} required>
                        <CustomDatePicker
                            date={editedTransaction.transactionDate || null}
                            onDateChange={(date) => {
                                if (date && !viewOnly) changeNewBankTransaction('transactionDate', date);
                            }}
                            height="40px"
                        />
                    </XtramField>
                    <XtramField label={amountLabel} required>
                        <XtramInput
                            direction={direction}
                            disabled={viewOnly}
                            type="number"
                            placeholder={amountLabel}
                            value={editedTransaction.amount || ''}
                            onChange={(e) => changeNewBankTransaction('amount', e.target.value)}
                        />
                    </XtramField>

                    {!viewOnly && (
                        <XtramField label={t('Document')} className={styles.fullRow}>
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept="application/pdf,image/*"
                                onChange={handleDocumentSelected}
                            />
                            <div className={styles.documentActions}>
                                {editedTransaction?.documentUrl && (
                                    <XtramButton
                                        variant="ghost"
                                        small
                                        onClick={() => setShowDocument((prev) => !prev)}
                                        disabled={documentLoading}
                                    >
                                        {showDocumentPane ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                        {showDocumentPane ? t('HideDocument') : t('ViewDocument')}
                                    </XtramButton>
                                )}
                                <XtramButton
                                    variant="secondary"
                                    small
                                    onClick={handleUploadDocumentClick}
                                    disabled={documentLoading}
                                    busy={documentLoading}
                                >
                                    <UploadFileIcon fontSize="small" />
                                    {editedTransaction?.documentUrl ? t('ReplaceDocument') : t('UploadDocument')}
                                </XtramButton>
                                {editedTransaction?.documentUrl && (
                                    <XtramButton variant="danger" small onClick={handleDeleteDocument} disabled={documentLoading}>
                                        <DeleteIcon fontSize="small" />
                                        {t('Delete')}
                                    </XtramButton>
                                )}
                            </div>
                        </XtramField>
                    )}
                </div>

                {showDocumentPane && (
                    <div className={styles.preview}>
                        <div className={styles.previewHeader}>
                            <span>{t('Document')}</span>
                            <div className={styles.previewActions}>
                                {editedTransaction?.documentUrl && (
                                    <Tooltip title={t('OpenInNewTab')}>
                                        <span>
                                            <IconButton size="small" className={styles.iconBtn} onClick={handleViewDocument} disabled={documentLoading}>
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                {!viewOnly && (
                                    <Tooltip title={t('HideDocument')}>
                                        <IconButton size="small" className={styles.iconBtn} onClick={() => setShowDocument(false)}>
                                            <VisibilityOffIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                        <iframe
                            key={resolvedDocumentUrl || 'no-document'}
                            title="external-transaction-document"
                            src={iframeSrc}
                            className={styles.frame}
                        />
                    </div>
                )}
            </div>
        </XtramModal>
    );
};

export default EditExternalTransactionDialog;
